// @ts-strict-ignore
/**
 * @fileoverview Free trial registration form for prospects to sign up for a free trial
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "@braze/beacon-fonts/Aribau.css";
import { ProgressTracker } from "@braze/beacon-labs-progress-tracker";
import BeaconThemeProvider from "@braze/beacon-styling";
import { rebrandTheme } from "@braze/beacon-theme";
import { useI18nLocale } from "@braze/i18n-core";

import useI18nNamespace from "@services/useI18nNamespace";

import { useBrazeFreeTrialSdk } from "@scenes/FreeTrial/hooks";
import { useFreeTrialSubmitPreVerification } from "@scenes/FreeTrial/hooks/useFreeTrialSubmitPreVerification";

import { RegistrationInfo } from "./RegistrationInfo/RegistrationInfo";
import type { MarketoParams } from "./UtmAttribution/types";
import type { BusinessDetailsType } from "./BusinessDetails";
import { BusinessDetails } from "./BusinessDetails";
import { Footer } from "./Footer";
import {
  BackgroundGradient,
  BackgroundImage,
  Content,
  FormContainer,
  MainContent,
  Overlay,
  PageBackground,
  StyledHeading,
  LanguagePickerWrapper,
} from "./FreeTrialRegistration.style";
import { Header } from "./Header";
import { LanguagePicker } from "./LanguagePicker";
import type { ProspectDetailsType } from "./ProspectDetails";
import { ProspectDetails } from "./ProspectDetails";
import { SalesBanner } from "./SalesBanner";
import {
  handleMarketoQueryParams,
  UpdateUtmAttributionValues,
  useLoadUtmAttributionScript,
} from "./UtmAttribution";
import { VerificationSuccess } from "./VerificationSuccess";
import type { VerifyContactDetailsType } from "./VerifyContactDetails";
import { VerifyContactDetails } from "./VerifyContactDetails";
import { getPhoneCodeFromCountryStringCode, getDefaultCountryCodeByLocale } from "./utils";

export const FreeTrialRegistration = (): React.ReactElement => {
  const { t } = useI18nNamespace("free-trial-registration");
  const currentLocale = useI18nLocale();

  const [stepIndex, setStepIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showSalesBanner, setShowSalesBanner] = useState(false);
  const [brazePageViewLogged, setBrazePageViewLogged] = useState(false);

  const { utmReady } = useLoadUtmAttributionScript();

  const stepConfigs = useMemo(
    () => [
      {
        stepName: t("your-details-tab-title", "Your details"),
        complete: stepIndex > 0,
      },
      {
        stepName: t("your-business-tab-title", "Your business"),
        complete: stepIndex > 1,
      },
      {
        stepName: t("verify-contact-information-tab-title", "Verify Contact Information"),
        complete: stepIndex > 2,
      },
      {
        stepName: t("verify-success-tab-title", "Complete"),
        complete: stepIndex > 3,
      },
    ],
    [t, stepIndex]
  );

  const defaultCountryCode = useMemo(() => getDefaultCountryCodeByLocale(currentLocale), [currentLocale]);

  const [prospectDetails, setProspectDetails] = useState<ProspectDetailsType>({
    firstName: "",
    lastName: "",
    jobTitle: "",
    company: "",
    department: "",
    email: "",
    phoneNumberCountry: defaultCountryCode,
    phoneNumber: "",
  });
  const [businessDetails, setBusinessDetails] = useState<BusinessDetailsType>({
    industry: "",
    companySize: "",
    country: "",
    usersCount: "",
    channels: [],
    goals: [],
    optInMarketing: false,
    features: [],
  });

  const marketoParams = useRef<MarketoParams>(handleMarketoQueryParams());

  useEffect(() => {
    if (utmReady) {
      UpdateUtmAttributionValues(marketoParams).then((umtMarketoParams) => {
        marketoParams.current = umtMarketoParams;
      });
    }
  }, [utmReady]);

  const { logBrazeFreeTrialEvent, trackBrazeFreeTrialPageView, updateBrazeFreeTrialUser } = useBrazeFreeTrialSdk();

  useEffect(() => {
    if (!brazePageViewLogged) {
      trackBrazeFreeTrialPageView();
      setBrazePageViewLogged(true);
    }
  }, [brazePageViewLogged, trackBrazeFreeTrialPageView]);

  const { mutate, isLoading } = useFreeTrialSubmitPreVerification({
    onError: setErrorMessage,
    onSuccess: () => {
      goToNextStep();
    },
  });

  const handleStepClick = useCallback(
    (index: number) => {
      // Once completed, do not allow user to go back
      if (stepIndex > 1) {
        return;
      }
      setStepIndex(index);
    },
    [stepIndex, setStepIndex]
  );

  const goToNextStep = useCallback(() => {
    setStepIndex(stepIndex + 1);
  }, [setStepIndex, stepIndex]);

  const handleOnProspectDetailsSubmit = useCallback(
    (details: ProspectDetailsType) => {
      setProspectDetails(details);
      goToNextStep();
    },
    [setProspectDetails, goToNextStep]
  );

  const handleOnBusinessDetailsSubmit = useCallback(
    (details: BusinessDetailsType) => {
      setBusinessDetails(details);
      setErrorMessage("");
      logBrazeFreeTrialEvent("pm.trial_registration_form_interaction_event", {
        interaction_type: "started",
      });
      mutate({
        email: prospectDetails.email,
        first_name: prospectDetails.firstName,
        last_name: prospectDetails.lastName,
        department: prospectDetails.department,
        job_title: prospectDetails.jobTitle,
        country_code: getPhoneCodeFromCountryStringCode(prospectDetails.phoneNumberCountry),
        phone_number: prospectDetails.phoneNumber,
        company: prospectDetails.company,
        company_size: details.companySize,
        country: details.country,
        industry: details.industry,
        locale: currentLocale,

        utm_program_medium_most_recent: marketoParams.current.INT_UTM_Medium_Most_Recent__c,
        utm_program_source_most_recent: marketoParams.current.INT_UTM_Source_Most_Recent__c,
        utm_program_campaign_most_recent: marketoParams.current.INT_UTM_Campaign_Most_Recent__c,
        utm_program_content_most_recent: marketoParams.current.INT_UTM_Content_Most_Recent__c,
        utm_program_term_most_recent: marketoParams.current.INT_UTM_Term_Most_Recent__c,

        utm_conversion_page_most_recent: marketoParams.current.INT_Conversion_Page_Most_Recent__c,
        utm_conversion_date_most_recent: marketoParams.current.INT_Conversion_Date_Most_Recent__c,

        utm_medium: marketoParams.current.utm_medium,
        utm_source: marketoParams.current.utm_source,
        utm_campaign: marketoParams.current.utm_campaign,
        utm_content: marketoParams.current.utm_content,
        utm_term: marketoParams.current.utm_term,

        gclid: marketoParams.current.GCLID__c,
        email_subscribe: details.optInMarketing,
      });
    },
    [logBrazeFreeTrialEvent, setBusinessDetails, mutate, prospectDetails, marketoParams, currentLocale]
  );

  const handleOnVerificationComplete = useCallback(
    (details: VerifyContactDetailsType) => {
      updateBrazeFreeTrialUser(details, prospectDetails, businessDetails);
      logBrazeFreeTrialEvent("pm.trial_registration_form_interaction_event", {
        interaction_type: "submitted",
      });
      goToNextStep();
    },
    [businessDetails, logBrazeFreeTrialEvent, prospectDetails, updateBrazeFreeTrialUser, goToNextStep]
  );

  return (
    // theme provider for new theme, needs to be removed after braze transitions to new theme
    <BeaconThemeProvider theme={rebrandTheme}>
      <PageBackground>
        <BackgroundGradient />
        <BackgroundImage />
        {showSalesBanner && <SalesBanner />}
        <Header isShowingSalesBanner={showSalesBanner} />
        <MainContent>
          <StyledHeading>
            {t("registration-info-header", "Get started today with a free 14-day trial")}
          </StyledHeading>
          <Content direction="column">
            <RegistrationInfo />
          </Content>
          <Content direction="column">
            <LanguagePickerWrapper>
              <LanguagePicker />
            </LanguagePickerWrapper>
            <Overlay>
              <FormContainer>
                <ProgressTracker
                  activeStep={stepIndex}
                  onStepClick={handleStepClick}
                  disableFutureSteps
                  stepConfigs={stepConfigs.slice(0, -2)}
                />
                {stepIndex === 0 && (
                  <ProspectDetails
                    prospectDetails={prospectDetails}
                    onSubmit={handleOnProspectDetailsSubmit}
                    setShowSalesBanner={setShowSalesBanner}
                  />
                )}
                {stepIndex === 1 && (
                  <BusinessDetails
                    businessDetails={businessDetails}
                    onSubmit={handleOnBusinessDetailsSubmit}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                  />
                )}
                {stepIndex === 2 && (
                  <VerifyContactDetails
                    contactDetails={{
                      email: prospectDetails.email,
                      phoneNumberCountry: prospectDetails.phoneNumberCountry,
                      phoneNumber: prospectDetails.phoneNumber,
                    }}
                    onSubmit={handleOnVerificationComplete}
                    setShowSalesBanner={setShowSalesBanner}
                  />
                )}
                {stepIndex === 3 && (
                  <VerificationSuccess
                    businessDetails={businessDetails}
                    prospectDetails={prospectDetails}
                    marketoParams={marketoParams.current}
                  />
                )}
              </FormContainer>
            </Overlay>
          </Content>
        </MainContent>
        <Footer />
      </PageBackground>
    </BeaconThemeProvider>
  );
};
